import React from 'react';
import { useNavigate } from 'react-router-dom';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: '🤖',
      title: 'AI-Powered Lesson Summaries',
      description: 'Automatically generate clear, structured lesson notes with practice reminders, mindset messages, and key quotes using advanced AI technology.'
    },
    {
      icon: '🎵',
      title: 'Guided Practice Sessions',
      description: 'Structured practice sessions with teacher-assigned tasks, real-time progress tracking, and smart timer management.'
    },
    {
      icon: '🆓',
      title: 'Free Practice Mode',
      description: 'Flexible practice time with category timers for scales, exercises, studies, repertoire, and ensemble work.'
    },
    {
      icon: '👥',
      title: 'Practice Buddies',
      description: 'Connect with friends! Send hearts, track progress together, and motivate each other in your musical journey.'
    },
    {
      icon: '📊',
      title: 'Practice Analytics',
      description: 'Detailed insights into practice habits, time spent per category, and progress over time with beautiful visualizations.'
    },
    {
      icon: '🏆',
      title: 'Achievement System',
      description: 'Earn badges, points, and awards for consistent practice, reaching goals, and completing lessons.'
    },
    {
      icon: '📰',
      title: 'Social Feed',
      description: 'Celebrate achievements with your buddies! Share progress, send encouragement, and stay motivated together.'
    },
    {
      icon: '🎯',
      title: 'Smart Goal Tracking',
      description: 'Set daily practice goals, track streaks, and earn points for consistent practice sessions.'
    },
    {
      icon: '👨‍🏫',
      title: 'Teacher Dashboard',
      description: 'Teachers can monitor student progress, assign tasks, provide feedback, and track lesson summaries.'
    }
  ];

  const benefits = [
    {
      icon: '🤖',
      title: 'Smart Lesson Notes',
      description: 'AI automatically creates clear, organized lesson summaries with practice reminders and key insights.'
    },
    {
      icon: '👥',
      title: 'Practice Buddies',
      description: 'Connect with friends, send hearts, and motivate each other to reach your musical goals together.'
    },
    {
      icon: '⏰',
      title: 'Daily Goal Tracking',
      description: 'Set and achieve your daily practice goals with visual progress indicators and streak tracking.'
    },
    {
      icon: '📈',
      title: 'Progress Visualization',
      description: 'See your improvement over time with detailed charts, analytics, and achievement tracking.'
    },
    {
      icon: '🎯',
      title: 'Focused Practice',
      description: 'Stay motivated with structured sessions, category timers, and personalized practice plans.'
    },
    {
      icon: '🎉',
      title: 'Celebrate Achievements',
      description: 'Earn badges, points, and recognition for your hard work and consistent practice efforts.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        {/* Mobile Layout - Combined hero card */}
        <div className="sm:hidden">
          {/* Video Section - Hidden on mobile, shown on larger screens */}
          <div className="hidden sm:block relative w-full" style={{ aspectRatio: '3/1' }}>
            <video
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="/mpbsplash1.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            
            {/* Purple gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/70 via-purple-600/70 to-pink-600/70"></div>
            
            {/* Text overlay on bottom part of video */}
            <div className="absolute inset-0 flex items-end justify-center pb-4">
              <div className="text-center px-4">
                <p className="text-sm text-white/90 leading-relaxed drop-shadow-lg">
                  Transform your music practice with our helpful planner, achievement tracking, and smart analytics!
                </p>
              </div>
            </div>
          </div>
          
          {/* Mobile Hero Card - Combined title and content */}
          <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 py-8 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl font-bold text-white mb-4 leading-tight drop-shadow-2xl">
                Mindful Practice Buddy
              </h1>
              <p className="text-base text-white/90 mb-6 leading-relaxed drop-shadow-lg">
                Transform your music practice with our helpful planner, achievement tracking, and smart analytics!
              </p>
              
              <div className="flex flex-col gap-3 justify-center items-center">
                <button
                  onClick={() => navigate('/signup')}
                  className="group relative bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 overflow-hidden w-full"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center">
                    <div className="text-xl mr-3 group-hover:animate-bounce">🚀</div>
                    <span>Start Your Musical Journey!</span>
                  </div>
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-indigo-600 transition-all duration-200 backdrop-blur-sm w-full"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </div>

      {/* Desktop/Tablet Layout - Video with overlay */}
      <div className="hidden sm:block relative overflow-hidden">
        {/* Video Background */}
        <div className="relative w-full">
          <div className="relative w-full" style={{ aspectRatio: '3/1', maxHeight: '100vh' }}>
            <video
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="/mpbsplash1.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          
            {/* Overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/70 via-purple-600/70 to-pink-600/70"></div>
            
            {/* Decorative background elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-white/10 to-transparent rounded-full -translate-y-48 translate-x-48"></div>
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-white/10 to-transparent rounded-full translate-y-40 -translate-x-40"></div>
          </div>
        </div>
        
        {/* Content Overlay - Positioned lower on the video */}
        <div className="absolute inset-0 flex items-end justify-center pb-4 sm:pb-16" style={{ aspectRatio: '3/1', maxHeight: '100vh' }}>
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
            <div className="text-center">
              <div className="mb-4 sm:mb-12">
                <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-2 sm:mb-6 leading-tight drop-shadow-2xl">
                  Mindful Practice Buddy
                </h1>
                          <p className="text-xs sm:text-base md:text-lg lg:text-xl xl:text-2xl text-white/90 mb-3 sm:mb-8 max-w-4xl mx-auto leading-relaxed drop-shadow-lg px-2">
                            Transform your music practice with our helpful planner, achievement tracking, and smart analytics!
                          </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-2">
                <button
                  onClick={() => navigate('/signup')}
                  className="group relative bg-white text-indigo-600 px-4 sm:px-8 md:px-10 py-2 sm:py-4 md:py-5 rounded-lg sm:rounded-2xl font-bold text-sm sm:text-lg md:text-xl shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 overflow-hidden w-full sm:w-auto"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center">
                    <div className="text-sm sm:text-2xl mr-1 sm:mr-3 group-hover:animate-bounce">🚀</div>
                    <span className="text-xs sm:text-base md:text-lg">Start Your Musical Journey!</span>
                  </div>
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="bg-transparent border-2 border-white text-white px-4 sm:px-8 md:px-10 py-2 sm:py-4 md:py-5 rounded-lg sm:rounded-xl font-bold text-xs sm:text-base md:text-lg hover:bg-white hover:text-indigo-600 transition-all duration-200 backdrop-blur-sm drop-shadow-lg w-full sm:w-auto"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

        {/* Features Section */}
        <div className="py-4 sm:py-6 md:py-8 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-32 sm:w-64 h-32 sm:h-64 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full -translate-y-16 sm:-translate-y-32 -translate-x-16 sm:-translate-x-32 opacity-20"></div>
        <div className="absolute bottom-0 right-0 w-40 sm:w-80 h-40 sm:h-80 bg-gradient-to-tr from-pink-200 to-rose-200 rounded-full translate-y-20 sm:translate-y-40 translate-x-20 sm:translate-x-40 opacity-20"></div>
        
        <div className="relative max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="text-center mb-10 sm:mb-16 md:mb-20">
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="relative">
                <div className="text-3xl sm:text-4xl md:text-6xl animate-pulse">✨</div>
                <div className="absolute -top-1 sm:-top-2 -right-1 sm:-right-2 w-2 sm:w-4 h-2 sm:h-4 bg-blue-400 rounded-full animate-ping"></div>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-3 sm:mb-6 px-2">
              Everything You Need for Amazing Practice! 🎵
            </h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed px-2">
              Mindful Practice Buddy combines AI-powered lesson summaries, practice buddies, achievement tracking, and smart analytics to transform your musical journey!
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white/80 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl shadow-lg sm:shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-white/50 overflow-hidden"
              >
                {/* Decorative gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-purple-50/50 to-pink-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative">
                  <div className="text-3xl sm:text-4xl md:text-5xl mb-3 sm:mb-4 md:mb-6 group-hover:animate-bounce">{feature.icon}</div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4 group-hover:text-purple-600 transition-colors">{feature.title}</h3>
                  <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-12 sm:py-16 md:py-24 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-36 sm:w-72 h-36 sm:h-72 bg-gradient-to-br from-green-200 to-emerald-200 rounded-full -translate-y-18 sm:-translate-y-36 translate-x-18 sm:translate-x-36 opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-32 sm:w-64 h-32 sm:h-64 bg-gradient-to-tr from-teal-200 to-cyan-200 rounded-full translate-y-16 sm:translate-y-32 -translate-x-16 sm:-translate-x-32 opacity-20"></div>
        
        <div className="relative max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="text-center mb-10 sm:mb-16 md:mb-20">
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="relative">
                <div className="text-3xl sm:text-4xl md:text-6xl animate-bounce">🌟</div>
                <div className="absolute -top-1 sm:-top-2 -right-1 sm:-right-2 w-2 sm:w-4 h-2 sm:h-4 bg-green-400 rounded-full animate-ping"></div>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent mb-3 sm:mb-6 px-2">
              Why Choose Mindful Practice Buddy? 🎯
            </h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed px-2">
              Join thousands of musicians who have transformed their practice routine with AI-powered lesson summaries, practice buddies, and our innovative approach to musical learning!
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group text-center"
              >
                <div className="relative bg-white/80 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl shadow-lg sm:shadow-xl group-hover:shadow-2xl transform group-hover:scale-105 transition-all duration-300 border border-white/50 overflow-hidden">
                  {/* Decorative gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-emerald-50/50 to-teal-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className="relative">
                    <div className="text-3xl sm:text-4xl md:text-5xl mb-3 sm:mb-4 md:mb-6 group-hover:animate-bounce">{benefit.icon}</div>
                    <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4 group-hover:text-emerald-600 transition-colors">{benefit.title}</h3>
                    <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-12 sm:py-16 md:py-24 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-5 sm:top-10 left-5 sm:left-10 w-16 sm:w-32 h-16 sm:h-32 bg-gradient-to-br from-white/10 to-transparent rounded-full animate-pulse"></div>
          <div className="absolute top-10 sm:top-20 right-10 sm:right-20 w-12 sm:w-24 h-12 sm:h-24 bg-gradient-to-br from-white/10 to-transparent rounded-full animate-bounce"></div>
          <div className="absolute bottom-10 sm:bottom-20 left-10 sm:left-20 w-14 sm:w-28 h-14 sm:h-28 bg-gradient-to-br from-white/10 to-transparent rounded-full animate-pulse"></div>
          <div className="absolute bottom-5 sm:bottom-10 right-5 sm:right-10 w-10 sm:w-20 h-10 sm:h-20 bg-gradient-to-br from-white/10 to-transparent rounded-full animate-bounce"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto text-center px-3 sm:px-4 lg:px-8">
          <div className="flex justify-center mb-4 sm:mb-8">
            <div className="relative">
              <div className="text-4xl sm:text-6xl md:text-8xl animate-bounce">🎉</div>
              <div className="absolute -top-1 sm:-top-2 -right-1 sm:-right-2 w-3 sm:w-6 h-3 sm:h-6 bg-yellow-300 rounded-full animate-ping"></div>
              <div className="absolute -bottom-1 sm:-bottom-2 -left-1 sm:-left-2 w-2 sm:w-4 h-2 sm:h-4 bg-orange-300 rounded-full animate-pulse"></div>
            </div>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6 md:mb-8 leading-tight px-2">
            Ready to Transform Your Practice? 🚀
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-white/90 mb-6 sm:mb-8 md:mb-12 max-w-4xl mx-auto leading-relaxed px-2">
            Join the amazing community of musicians who are achieving their goals with AI-powered lesson summaries, practice buddies, and Mindful Practice Buddy! 🎵
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 justify-center items-center px-2">
            <button
              onClick={() => navigate('/signup')}
              className="group relative bg-white text-indigo-600 px-6 sm:px-10 md:px-12 py-3 sm:py-5 md:py-6 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg md:text-xl shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 overflow-hidden w-full sm:w-auto"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative flex items-center justify-center">
                <div className="text-lg sm:text-2xl mr-2 sm:mr-3 group-hover:animate-bounce">🎵</div>
                <span className="text-sm sm:text-base md:text-lg">Start Your Musical Journey!</span>
              </div>
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-transparent border-2 border-white text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-lg sm:rounded-xl font-bold text-sm sm:text-base md:text-lg hover:bg-white hover:text-indigo-600 transition-all duration-200 backdrop-blur-sm w-full sm:w-auto"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="text-center">
            <div className="text-2xl sm:text-3xl mb-3 sm:mb-4">🎼</div>
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2">Mindful Practice Buddy</h3>
            <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6 px-2">
              Empowering musicians to practice with purpose and achieve their goals.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6">
              <div className="flex space-x-4 sm:space-x-6">
                <button
                  onClick={() => navigate('/signup')}
                  className="text-indigo-400 hover:text-indigo-300 transition-colors duration-200 text-sm sm:text-base"
                >
                  Sign Up
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="text-indigo-400 hover:text-indigo-300 transition-colors duration-200 text-sm sm:text-base"
                >
                  Sign In
                </button>
              </div>
              <div className="flex space-x-4 sm:space-x-6 mt-2 sm:mt-0">
                <a
                  href="/privacy-policy"
                  className="text-gray-400 hover:text-gray-300 transition-colors duration-200 text-xs sm:text-sm"
                >
                  Privacy Policy
                </a>
                <a
                  href="/terms-of-use"
                  className="text-gray-400 hover:text-gray-300 transition-colors duration-200 text-xs sm:text-sm"
                >
                  Terms of Use
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
